import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, Truck, Moon, ChevronLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import StarRating from "@/components/StarRating";
import MoonDivider from "@/components/MoonDivider";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [color, setColor] = useState("");
  const [chocType, setChocType] = useState("");
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, comment: "" });
  const [notFound, setNotFound] = useState(false);

  const load = () => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data); setActiveImg(0); setColor(""); setChocType("");
    }).catch(() => setNotFound(true));
    api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  };

  useEffect(() => { load(); window.scrollTo(0, 0); }, [id]);

  const variants = product?.variants || [];

  const colors = useMemo(() => {
    const seen = [];
    variants.forEach((v) => {
      if (!seen.find((c) => c.color === v.color)) {
        seen.push({ color: v.color, price: v.price, image_index: v.image_index });
      }
    });
    return seen;
  }, [variants]);

  const chocTypes = useMemo(() => [...new Set(variants.map((v) => v.chocolate_type))], [variants]);

  const selectedVariant = useMemo(
    () => variants.find((v) => v.color === color && v.chocolate_type === chocType),
    [variants, color, chocType]
  );

  const hasVariants = variants.length > 0;
  const minPrice = colors.length ? Math.min(...colors.map((c) => c.price)) : product?.price;
  const displayPrice = selectedVariant ? selectedVariant.price
    : color ? colors.find((c) => c.color === color)?.price
    : minPrice;

  const chooseColor = (c) => {
    setColor(c.color);
    setActiveImg(c.image_index ?? 0);
  };

  // stock context depends on variant selection
  const variantStock = selectedVariant ? selectedVariant.stock : null;
  const stockForColorType = (col, t) => variants.find((v) => v.color === col && v.chocolate_type === t)?.stock ?? 0;

  const overallSoldOut = hasVariants
    ? variants.every((v) => (v.stock ?? 0) <= 0)
    : (product?.stock ?? 0) <= 0;

  const canAdd = hasVariants
    ? Boolean(selectedVariant && selectedVariant.stock > 0)
    : (product?.stock ?? 0) > 0;

  const handleAdd = () => {
    if (hasVariants) {
      if (!color) return toast.error("Please choose a colour");
      if (!chocType) return toast.error("Please choose a chocolate type");
      if (!selectedVariant || selectedVariant.stock <= 0) return toast.error("This option is sold out");
      addItem(product, qty, selectedVariant);
    } else {
      addItem(product, qty);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.name.trim()) return toast.error("Please add your name");
    try {
      await api.post(`/products/${id}/reviews`, reviewForm);
      toast.success("Thank you for your review");
      setReviewForm({ name: "", rating: 5, comment: "" });
      load();
    } catch {
      toast.error("Could not submit review");
    }
  };

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="product-not-found">
      <p className="font-serif text-3xl text-white/50 mb-6">This temptation has vanished.</p>
      <Link to="/shop" className="text-gold font-body uppercase tracking-widest text-xs border-b border-gold/40 pb-1">Back to Shop</Link>
    </div>
  );
  if (!product) return <p className="text-center font-serif text-2xl text-white/40 py-32">Unveiling…</p>;

  const images = product.images && product.images.length ? product.images : [""];

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="product-detail-page">
      <Link to="/shop" className="inline-flex items-center gap-1.5 font-body uppercase tracking-widest text-xs text-white/50 hover:text-gold mb-8">
        <ChevronLeft className="w-4 h-4" /> Back
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <motion.div key={activeImg} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
            className="aspect-square bg-noir-2 overflow-hidden border border-white/5">
            {images[activeImg] ? (
              <img src={mediaUrl(images[activeImg])} alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />
            ) : <div className="w-full h-full flex items-center justify-center text-white/20">No image</div>}
          </motion.div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                  className={`w-20 h-20 overflow-hidden border ${activeImg === i ? "border-gold" : "border-white/10"}`}>
                  <img src={mediaUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {product.collection && (
            <p className="font-body uppercase tracking-[0.3em] text-xs text-gold mb-4">{product.collection}</p>
          )}
          <h1 className="font-serif text-4xl sm:text-5xl text-cream leading-tight mb-4">{product.name}</h1>

          <div className="flex items-center gap-4 mb-6">
            <StarRating value={product.rating} size={16} />
            <span className="font-body text-sm text-white/40">
              {product.review_count > 0 ? `${product.rating} · ${product.review_count} reviews` : "No reviews yet"}
            </span>
          </div>

          <p className="font-serif text-3xl text-gold mb-6" data-testid="product-price">
            {hasVariants && !selectedVariant && !color ? `From ${money(displayPrice)}` : money(displayPrice)}
          </p>
          <p className="font-body text-white/60 leading-relaxed mb-8">{product.description || product.short_description}</p>

          {/* VARIANT: COLOUR */}
          {hasVariants && (
            <div className="mb-6" data-testid="variant-color-group">
              <p className="font-body uppercase tracking-[0.2em] text-[11px] text-champagne mb-3">
                Colour {color && <span className="text-white/40 normal-case tracking-normal">— {color}</span>}
              </p>
              <div className="flex flex-wrap gap-3">
                {colors.map((c) => {
                  const colorSoldOut = !variants.some((v) => v.color === c.color && v.stock > 0);
                  return (
                    <button key={c.color} disabled={colorSoldOut} onClick={() => chooseColor(c)}
                      data-testid={`color-${c.color.toLowerCase()}`}
                      className={`px-5 py-3 border font-body text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${color === c.color ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-white/70 hover:border-gold/50"}`}>
                      <span className="flex items-center gap-2">
                        {color === c.color && <Check className="w-3.5 h-3.5" />}
                        {c.color} · {money(c.price)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* VARIANT: CHOCOLATE TYPE */}
          {hasVariants && (
            <div className="mb-6" data-testid="variant-type-group">
              <p className="font-body uppercase tracking-[0.2em] text-[11px] text-champagne mb-3">
                Chocolate Type {chocType && <span className="text-white/40 normal-case tracking-normal">— {chocType}</span>}
              </p>
              <div className="flex flex-wrap gap-3">
                {chocTypes.map((t) => {
                  const disabled = color ? stockForColorType(color, t) <= 0 : false;
                  return (
                    <button key={t} disabled={disabled} onClick={() => setChocType(t)}
                      data-testid={`type-${t.toLowerCase()}`}
                      className={`px-5 py-3 border font-body text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${chocType === t ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-white/70 hover:border-gold/50"}`}>
                      <span className="flex items-center gap-2">
                        {chocType === t && <Check className="w-3.5 h-3.5" />}
                        {t} Chocolate
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STOCK STATUS */}
          <div className="flex items-center gap-2 mb-8">
            {overallSoldOut ? (
              <span className="font-body text-sm text-white/50 border border-white/20 px-3 py-1" data-testid="stock-status">Sold Out</span>
            ) : hasVariants && selectedVariant ? (
              selectedVariant.stock <= 0 ? (
                <span className="font-body text-sm text-white/50 border border-white/20 px-3 py-1" data-testid="stock-status">This option is sold out</span>
              ) : selectedVariant.stock <= 5 ? (
                <span className="font-body text-sm text-gold border border-gold/40 px-3 py-1" data-testid="stock-status">Only {selectedVariant.stock} left</span>
              ) : (
                <span className="font-body text-sm text-white/50 border border-white/15 px-3 py-1" data-testid="stock-status">In Stock</span>
              )
            ) : hasVariants ? (
              <span className="font-body text-sm text-white/40 border border-white/15 px-3 py-1" data-testid="stock-status">Choose colour & chocolate</span>
            ) : (product.stock <= 5 ? (
              <span className="font-body text-sm text-gold border border-gold/40 px-3 py-1" data-testid="stock-status">Only {product.stock} left</span>
            ) : (
              <span className="font-body text-sm text-white/50 border border-white/15 px-3 py-1" data-testid="stock-status">In Stock</span>
            ))}
          </div>

          {!overallSoldOut && (
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="flex items-center border border-white/15">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-4 hover:text-gold" data-testid="qty-dec"><Minus className="w-4 h-4" /></button>
                <span className="font-body w-10 text-center" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="px-4 py-4 hover:text-gold" data-testid="qty-inc"><Plus className="w-4 h-4" /></button>
              </div>
              <button
                onClick={handleAdd}
                disabled={!canAdd}
                data-testid="add-to-cart-detail"
                className="flex-1 inline-flex items-center justify-center gap-2.5 bg-gold text-black font-body uppercase tracking-[0.2em] text-xs py-4 hover:bg-champagne transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ShoppingBag className="w-4 h-4" /> Add to Bag
              </button>
            </div>
          )}

          <div className="flex items-center gap-2.5 font-body text-sm text-white/50 border-t border-white/10 pt-6">
            <Truck className="w-4 h-4 text-gold" strokeWidth={1.5} />
            Complimentary shipping on orders over $75 · Discreet worldwide delivery
          </div>
        </div>
      </div>

      {/* REVIEWS */}
      <section className="mt-24">
        <MoonDivider label="Reviews & Ratings" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-12">
          <div>
            <h3 className="font-serif text-2xl text-cream mb-6">
              {reviews.length > 0 ? `${reviews.length} indulgent verdicts` : "Be the first to indulge"}
            </h3>
            <div className="space-y-6" data-testid="reviews-list">
              {reviews.map((r) => (
                <div key={r.id} className="border-b border-white/10 pb-5" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-serif text-lg text-cream">{r.name}</span>
                    <StarRating value={r.rating} size={14} />
                  </div>
                  {r.comment && <p className="font-body text-sm text-white/55 leading-relaxed">{r.comment}</p>}
                </div>
              ))}
              {reviews.length === 0 && <p className="font-body text-sm text-white/40">No reviews yet — share your thoughts.</p>}
            </div>
          </div>

          <div className="bg-noir-2 border border-white/10 p-8">
            <h3 className="font-serif text-2xl text-cream mb-6 flex items-center gap-2"><Moon className="w-5 h-5 text-gold" strokeWidth={1.5} /> Leave a review</h3>
            <form onSubmit={submitReview} className="space-y-4" data-testid="review-form">
              <input placeholder="Your name" value={reviewForm.name} data-testid="review-name"
                onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                className="w-full bg-noir border border-white/15 px-4 py-3 font-body text-cream placeholder:text-white/30 focus:border-gold focus:outline-none" />
              <div className="flex items-center gap-3">
                <span className="font-body text-sm text-white/50">Rating</span>
                <StarRating value={reviewForm.rating} size={22} onChange={(v) => setReviewForm({ ...reviewForm, rating: v })} testid="review-rating" />
              </div>
              <textarea rows={4} placeholder="Tell us how it made you feel…" value={reviewForm.comment} data-testid="review-comment"
                onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                className="w-full bg-noir border border-white/15 px-4 py-3 font-body text-cream placeholder:text-white/30 focus:border-gold focus:outline-none" />
              <button type="submit" data-testid="review-submit"
                className="w-full py-3.5 bg-gold text-black font-body uppercase tracking-[0.2em] text-xs hover:bg-champagne transition-colors">
                Submit Review
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
